/** The CTI call-state machine (useReducer). Single source of truth for the UI;
 *  the telephony provider drives transitions via dispatched actions, never the
 *  other way around. Kept pure + exhaustively-typed so it's trivially testable. */
import type { Call, CallOutcome, CallStatus, Contact, Direction, HistoryEntry } from "../telephony/types";

export type AgentStatus = "available" | "busy" | "offline";
export type AuthStatus = "loggedOut" | "loggingIn" | "loggedIn";
export interface AgentIdentity { id: string; name: string; }

export interface CallState {
  status: CallStatus;
  call: Call | null;
  history: HistoryEntry[];
  agentStatus: AgentStatus;
  auth: { status: AuthStatus; agent: AgentIdentity | null; error: string | null };
  error: string | null;
}

export const initialState: CallState = {
  status: "idle",
  call: null,
  history: [],
  agentStatus: "offline",   // must log in, then go online
  auth: { status: "loggedOut", agent: null, error: null },
  error: null,
};

export type Action =
  | { type: "DIAL_START"; callId: string; contact: Contact; atMs: number }
  | { type: "INCOMING"; callId: string; contact: Contact; atMs: number }
  | { type: "RINGING"; callId: string }
  | { type: "CONNECTED"; callId: string; atMs: number }
  | { type: "ENDED"; callId: string; outcome: CallOutcome; atMs: number }
  | { type: "CLEAR" }
  | { type: "TOGGLE_MUTE" }
  | { type: "TOGGLE_HOLD" }
  | { type: "TOGGLE_RECORDING" }
  | { type: "SET_NOTES"; notes: string }
  | { type: "SET_DISPOSITION"; disposition: string }
  | { type: "ENRICH_CONTACT"; patch: Partial<Contact> }
  | { type: "SET_AGENT_STATUS"; status: AgentStatus }
  | { type: "LOAD_HISTORY"; history: HistoryEntry[] }
  | { type: "LOGIN_START" }
  | { type: "LOGIN_OK"; agent: AgentIdentity }
  | { type: "LOGIN_ERR"; message: string }
  | { type: "LOGOUT" }
  | { type: "SET_ERROR"; message: string | null };

function newCall(id: string, direction: Direction, contact: Contact, status: CallStatus, atMs: number): Call {
  return { id, direction, contact, status, startedAtMs: atMs, muted: false, onHold: false, recording: false, notes: "" };
}

function toHistory(call: Call, outcome: CallOutcome, endedAtMs: number): HistoryEntry {
  const durationSec = call.connectedAtMs ? Math.max(0, Math.round((endedAtMs - call.connectedAtMs) / 1000)) : 0;
  return { id: call.id, direction: call.direction, contact: call.contact, startedAtMs: call.startedAtMs, durationSec, outcome };
}

export function callReducer(state: CallState, action: Action): CallState {
  switch (action.type) {
    case "DIAL_START":
      return { ...state, status: "dialing", error: null,
        call: newCall(action.callId, "outbound", action.contact, "dialing", action.atMs) };

    case "INCOMING":
      // A new inbound call supersedes any wrap-up ("ended") state.
      return { ...state, status: "incoming", error: null,
        call: newCall(action.callId, "inbound", action.contact, "incoming", action.atMs) };

    case "RINGING":
      if (!state.call || state.call.id !== action.callId) return state;
      return { ...state, status: "ringing", call: { ...state.call, status: "ringing" } };

    case "CONNECTED":
      if (!state.call || state.call.id !== action.callId) return state;
      return { ...state, status: "active", agentStatus: "busy",
        call: { ...state.call, status: "active", connectedAtMs: action.atMs } };

    case "ENDED": {
      if (!state.call || state.call.id !== action.callId) return state;
      const ended: Call = { ...state.call, status: "ended", endedAtMs: action.atMs, outcome: action.outcome };
      return { ...state, status: "ended", agentStatus: "available", call: ended,
        history: [toHistory(ended, action.outcome, action.atMs), ...state.history].slice(0, 100) };
    }

    case "CLEAR":
      return { ...state, status: "idle", call: null };

    case "TOGGLE_MUTE":
      if (!state.call) return state;
      return { ...state, call: { ...state.call, muted: !state.call.muted } };

    case "TOGGLE_HOLD": {
      if (!state.call || (state.status !== "active" && state.status !== "onHold")) return state;
      const onHold = !state.call.onHold;
      return { ...state, status: onHold ? "onHold" : "active", call: { ...state.call, onHold } };
    }

    case "TOGGLE_RECORDING":
      if (!state.call) return state;
      return { ...state, call: { ...state.call, recording: !state.call.recording } };

    case "SET_NOTES":
      if (!state.call) return state;
      return { ...state, call: { ...state.call, notes: action.notes } };

    case "SET_DISPOSITION":
      if (!state.call) return state;
      return { ...state, call: { ...state.call, disposition: action.disposition } };

    case "ENRICH_CONTACT":
      if (!state.call) return state;
      return { ...state, call: { ...state.call, contact: { ...state.call.contact, ...action.patch } } };

    case "SET_AGENT_STATUS":
      return { ...state, agentStatus: action.status };

    case "LOAD_HISTORY":
      return { ...state, history: action.history };

    case "LOGIN_START":
      return { ...state, auth: { ...state.auth, status: "loggingIn", error: null } };

    case "LOGIN_OK":
      // Logged in but starts OFFLINE — the agent explicitly goes online (RingCX flow).
      return { ...state, agentStatus: "offline", auth: { status: "loggedIn", agent: action.agent, error: null } };

    case "LOGIN_ERR":
      return { ...state, auth: { status: "loggedOut", agent: null, error: action.message } };

    case "LOGOUT":
      return { ...initialState, history: state.history };

    case "SET_ERROR":
      return { ...state, error: action.message };

    default:
      return state;
  }
}
